import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, ExternalLink } from "lucide-react";
import api, { backendUrl } from "@/lib/api";
import { PROPOSALS } from "@/constants/testIds/crm";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";
const SORT_OPTIONS = [
    { value: "-proposal_date", label: "Newest first" },
    { value: "proposal_date", label: "Oldest first" },
    { value: "amount", label: "Lowest amount" },
    { value: "-amount", label: "Highest amount" },
    { value: "status", label: "Status A-Z" },
];

function resolveFileUrl(fileUrl) {
    if (!fileUrl) return "";
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (fileUrl.startsWith("/")) return backendUrl(fileUrl);
    return backendUrl(`/${fileUrl}`);
}

export default function Proposals() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [leads, setLeads] = useState({});
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [sort, setSort] = useState("-proposal_date");

    const loadItems = useCallback(() => {
        const params = new URLSearchParams();
        params.set("page", page);
        params.set("limit", 10);
        if (q) params.set("q", q);
        if (status) params.set("status", status);
        if (sort) params.set("sort", sort);
        api.get(`/proposals?${params.toString()}`).then((r) => {
            const normalized = normalizeListResponse(r.data);
            setItems(normalized.items);
            setCounts(normalized.counts);
            setTotal(normalized.total);
        });
    }, [page, q, sort, status]);

    const loadLeads = useCallback(() => {
        api.get("/leads?limit=500").then((r) => {
            const m = {};
            r.data.items.forEach((l) => { m[l.id] = l; });
            setLeads(m);
        });
    }, []);
    useEffect(() => { loadItems(); }, [loadItems]);
    useEffect(() => { loadLeads(); }, [loadLeads]);

    const amountTotal = items.reduce((s, p) => s + (p.amount || 0), 0);
    const acceptedAmount = items.filter((p) => p.status === "accepted").reduce((s, p) => s + (p.amount || 0), 0);
    const statusOptions = useMemo(() => ["draft", "sent", "accepted", "rejected"], []);
    const countCards = [
        { label: "Total Proposals", value: total, hint: "All matching records" },
        { label: "Quoted Amount", value: `$${amountTotal.toLocaleString()}`, hint: "Current page total" },
        { label: "Accepted Amount", value: `$${acceptedAmount.toLocaleString()}`, hint: "Current page total" },
        { label: "Draft", value: counts.draft || 0 },
        { label: "Sent", value: counts.sent || 0 },
        { label: "Accepted", value: counts.accepted || 0 },
        { label: "Rejected", value: counts.rejected || 0 },
    ];

    const downloadFile = async (proposal) => {
        if (!proposal?.file_url) return;
        const url = resolveFileUrl(proposal.file_url);
        try {
            const response = await fetch(url, { credentials: "include" });
            if (!response.ok) throw new Error(`Download failed (${response.status})`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = proposal.file_name || url.split("/").pop() || "proposal-file";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <div className="space-y-6" data-testid={PROPOSALS.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Deals</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Proposals</h1>
                <p className="text-sm text-muted-foreground mt-2">Track every quoted number and its outcome. Add proposals from a lead detail page.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search notes, file or status…" className="md:col-span-2 h-10 px-3 rounded-md border border-input bg-card text-sm" />
                <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All statuses</option>
                    {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm md:col-span-3">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <StatusCountCards
                cards={countCards}
                className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3"
            />

            <div className="border border-border rounded-md bg-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                            <th className="px-4 py-3">Lead</th>
                            <th className="px-4 py-3">Date</th>
                            <th className="px-4 py-3">Amount</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">File</th>
                            <th className="px-4 py-3">Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No proposals yet.</td></tr>}
                        {items.map((p) => {
                            const lead = leads[p.lead_id];
                            return (
                                <tr key={p.id} data-testid={PROPOSALS.row} className={`border-b border-border last:border-0 hover:brightness-[0.99] transition-colors ${getStatusToneClasses(p.status, { group: "status", variant: "row" })}`}>
                                    <td className="px-4 py-3 font-medium">{lead?.company || "—"}</td>
                                    <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{new Date(p.proposal_date).toLocaleDateString()}</td>
                                    <td className="px-4 py-3 font-mono tabular-nums">${Number(p.amount).toLocaleString()}</td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium border ${getStatusToneClasses(p.status, { group: "status", variant: "badge" })}`}>{getStatusLabel(p.status)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {p.file_url ? (
                                            <button
                                                type="button"
                                                onClick={() => downloadFile(p)}
                                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                            >
                                                <FileText className="h-3.5 w-3.5" /> {p.file_name || "Open"}
                                                <ExternalLink className="h-3 w-3" />
                                            </button>
                                        ) : <span className="text-muted-foreground text-xs">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-muted-foreground text-xs max-w-xs truncate">{p.notes || "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <PaginationBar total={total} page={page} limit={10} onPageChange={setPage} />
            </div>
        </div>
    );
}
